import { useEffect } from "react";
import { getPreferences } from "@/lib/consent";
import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

export const useTracking = () => {
  useEffect(() => {
    // Only run after explicit consent
    const prefs = getPreferences();
    if (!prefs.analytics && !prefs.marketing) return;

    let active = true;
    void supabase
      .rpc("get_public_tracking_config")
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active || error || !data) return;

        if (prefs.analytics && data.ga4_enabled && data.ga4_measurement_id) {
          initializeGA4(data.ga4_measurement_id);
          if (data.performance_monitoring) trackWebVitals();
        }

        if (prefs.marketing && data.fb_pixel_enabled && data.fb_pixel_id) {
          initializeFacebookPixel(data.fb_pixel_id);
        }
      });

    return () => { active = false; };
  }, []);

  const initializeGA4 = (measurementId: string) => {
    // Check if already initialized
    if (document.querySelector(`script[src*="${measurementId}"]`)) return;

    // Add GA4 script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    gtag("js", new Date());
    gtag("config", measurementId, {
      anonymize_ip: true,
      cookie_flags: "SameSite=None;Secure",
    });

    window.gtag = gtag;
  };

  const initializeFacebookPixel = (pixelId: string) => {
    // Check if already initialized
    if (window.fbq) return;

    (window as any).fbq = function() {
      ((window as any).fbq.q = (window as any).fbq.q || []).push(arguments);
    };
    (window as any)._fbq = (window as any)._fbq || (window as any).fbq;
    (window as any).fbq.loaded = true;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);

    (window as any).fbq("init", pixelId);
    (window as any).fbq("track", "PageView");
  };

  const trackWebVitals = () => {
    // Track Core Web Vitals using web-vitals library approach
    try {
      // LCP - Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (window.gtag && 'renderTime' in lastEntry) {
          const value = (lastEntry as any).renderTime || (lastEntry as any).loadTime;
          window.gtag("event", "LCP", {
            event_category: "Web Vitals",
            value: Math.round(value),
            non_interaction: true,
          });
        }
      });
      lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });

      // FID - First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (window.gtag && entry.processingStart) {
            const value = entry.processingStart - entry.startTime;
            window.gtag("event", "FID", {
              event_category: "Web Vitals",
              value: Math.round(value),
              non_interaction: true,
            });
          }
        });
      });
      fidObserver.observe({ type: "first-input", buffered: true });

      // CLS - Cumulative Layout Shift
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput && entry.value) {
            clsValue += entry.value;
          }
        });
        if (window.gtag) {
          window.gtag("event", "CLS", {
            event_category: "Web Vitals",
            value: Math.round(clsValue * 1000),
            non_interaction: true,
          });
        }
      });
      clsObserver.observe({ type: "layout-shift", buffered: true });
    } catch (error) {
      console.error("Error tracking web vitals:", error);
    }
  };

  const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
    // Only run after explicit consent
    const prefs = getPreferences();
    if (!prefs.analytics && !prefs.marketing) return;

    // GA4 event
    if (window.gtag) {
      window.gtag("event", eventName, parameters);
    }

    // Facebook Pixel event
    if (window.fbq) {
      window.fbq("track", eventName, parameters);
    }
  };

  return { trackEvent };
};
